import { type FC, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BsArrowRight, BsArrowBarRight, BsArrowBarLeft } from 'react-icons/bs';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import DelayedDroppableWrapper from '@editor2/components/DelayedDroppableWrapper';
import { Badge } from 'react-bootstrap';

import { type RootState } from '@editor2/reducers/root';
import { fetchAllPagesWithElementsData } from '@editor2/reducers/editor/effects';
import { reorderBlock, headerContainer } from '../styles';
import { type DoubleElements, ElementType } from '@editor2/types/pageElements';
import { showToast } from '@editor2/reducers/toastSlice';
import { ToastSeverity } from '@editor2/types/toastSeverity';
import { type Chapter } from '@editor2/types/chapter';
import { repaginateElements } from '@editor2/reducers/editor/editorSlice';
import LoaderWrapper from '@editor2/components/LoaderWrapper';
import {
	getElementHumanName,
	getElementPreview,
	getElementSubtype,
	isDoubleElement
} from '@editor2/services/elementService';
import {
	validateDoubleNesting,
	validateDirectionOrder,
	validateAllowedElementsInside,
	validateIfInsideDouble
} from '@editor2/services/doubleElementValidationsService';
import { ChangesStatus } from '@editor2/types/changesStatus';
import {
	validatePageReorder,
	validatePageInsideDoubleElement,
	validateSplitDoubleElement
} from '@editor2/services/repaginatorValidationsService';

interface Props {
	chapterId: string;
	courseId: number;
}

const Repaginator: FC<Props> = (props) => {
	const { chapterId, courseId } = props;

	const dispatch = useDispatch();

	const showWarningToast = (message: string) => {
		dispatch(
			showToast({
				severity: ToastSeverity.Warning,
				message
			})
		);
	};

	const { chapters, page_groups, pages, elements, changes, headCommit, config } = useSelector(
		(state: RootState) => state.courses[courseId]
	);

	const getChapterPageGroupsPages = (chapter: Chapter) => {
		return [].concat(
			...chapter.page_groups.map((pageGroupId) => page_groups.entities[pageGroupId].pages)
		);
	};

	const concatRepaginatorItems = () => {
		const dragItems = chapterPages.map((pageId, pageIndex) => {
			const page = { ...pages.entities[pageId] };
			const pageElements = page.elements.map((elementId) => ({
				...elements.entities[elementId]
			}));

			return [page, ...pageElements];
		});

		return [].concat(...dragItems);
	};

	const chapter = chapters && chapters.entities[chapterId];
	const chapterPages = chapter.page_groups ? getChapterPageGroupsPages(chapter) : chapter.pages;

	const [items, setItems] = useState([]);

	useEffect(() => {
		const isAllPagesLoaded = chapterPages.every((pageId) => pages.entities[pageId].elements);

		if (!isAllPagesLoaded) {
			const pagesIds = [];
			chapterPages.forEach((familyId) => {
				const page = pages.entities[familyId];
				if (changes.pages[familyId]?.includes(ChangesStatus.New) || page.elements) return;

				pagesIds.push(page.id);
			});

			dispatch(fetchAllPagesWithElementsData({ bookId: headCommit.thing_id, courseId, pagesIds }));
		} else {
			setItems(concatRepaginatorItems());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chapter, pages, page_groups, elements]);

	const renumberItems = (items) => {
		let indent = false;

		return items.map((item) => {
			const updatedItem = {
				...item,
				indent:
					item.type === ElementType.NestingInstruction ? indent && item.direction === 'in' : indent
			};

			if (item.type === ElementType.NestingInstruction) indent = item.direction === 'in';

			return updatedItem;
		});
	};

	const createPagesPayload = (renumberedItems) => {
		let currentPage;

		return renumberedItems.reduce((acc, curr) => {
			if (curr.type === 'NG::Traditional::Page') {
				currentPage = curr.family_id;
				acc[currentPage] = [];
			} else {
				acc[currentPage].push(curr.family_id);
			}
			return acc;
		}, {});
	};

	const getElementsBefore = (index: number) => {
		const slice = items.slice(0, index).reverse();
		const pageIndex = slice.findIndex((el) => el.type === 'NG::Traditional::Page');

		return slice
			.slice(0, pageIndex)
			.map((el) => el.family_id)
			.reverse();
	};

	const getElementsAfter = (index: number) => {
		const slice = items.slice(index);
		const pageIndex = slice.findIndex((el) => el.type === 'NG::Traditional::Page');

		return pageIndex > -1
			? slice.slice(0, pageIndex).map((el) => el.family_id)
			: slice.map((el) => el.family_id);
	};

	const getPageIndex = (sourceIndex) => {
		const slice = items.slice(0, sourceIndex).reverse();
		const page = slice.find((el) => el.type === 'NG::Traditional::Page');
		const pageIndex = items.findIndex((item) => item.family_id === page.family_id);

		return pageIndex;
	};

	const getElementsWithoutPages = (items) => {
		return items.filter((el) => el.type !== 'NG::Traditional::Page').map((el) => el.family_id);
	};

	const findNearestElement = (destinationIndex) => {
		return (
			items.slice(destinationIndex).find((item) => item.type !== 'NG::Traditional::Page') ||
			items
				.slice(0, destinationIndex)
				.reverse()
				.find((item) => item.type !== 'NG::Traditional::Page')
		);
	};

	const getUpdatedDestinationIndex = (elementsWithoutPages, destinationIndex) => {
		const destinationItem = items[destinationIndex];

		if (destinationItem.type === 'NG::Traditional::Page') {
			const nearestElement = findNearestElement(destinationIndex);
			return elementsWithoutPages.indexOf(nearestElement.family_id);
		}

		return elementsWithoutPages.indexOf(destinationItem.family_id);
	};

	const onDragEnd = (result) => {
		const { source, destination, type } = result;
		const { index: sourceIndex } = source;
		const { index: destinationIndex } = destination;

		if (type === 'Page') {
			const dragItem = items[sourceIndex];

			try {
				if (dragItem.type === 'NG::Traditional::Page') {
					validatePageReorder(sourceIndex, destinationIndex, items);

					validatePageInsideDoubleElement(sourceIndex, destinationIndex, items[destinationIndex]);
				} else if (isDoubleElement(dragItem.type)) {
					const pageIndex = getPageIndex(sourceIndex);

					const validatePayload = {
						draggableElement: dragItem,
						pageElements: [...getElementsBefore(sourceIndex), ...getElementsAfter(sourceIndex)],
						sourceIndex: sourceIndex - pageIndex - 1,
						destinationIndex: destinationIndex - pageIndex - 1,
						elements
					};

					validateSplitDoubleElement(dragItem, sourceIndex, destinationIndex, items);

					validateDoubleNesting(validatePayload);

					validateDirectionOrder(validatePayload);

					validateAllowedElementsInside(validatePayload);
				} else {
					// not double element move
					const elementsWithoutPages = getElementsWithoutPages(items);

					const updatedSourceIndex = elementsWithoutPages.indexOf(items[sourceIndex].family_id);
					const updatedDestinationIndex = getUpdatedDestinationIndex(
						elementsWithoutPages,
						destinationIndex
					);

					const payload = {
						pageElements: elementsWithoutPages,
						sourceIndex: updatedSourceIndex,
						destinationIndex: updatedDestinationIndex,
						elements
					};

					validateIfInsideDouble(payload);
				}
			} catch (error) {
				showWarningToast(error.message);
				return;
			}

			const tempItems = [...items.slice(0, sourceIndex), ...items.slice(sourceIndex + 1)];
			const updatedItems = [
				...tempItems.slice(0, destinationIndex),
				dragItem,
				...tempItems.slice(destinationIndex)
			];

			const renumberedItems = renumberItems(updatedItems);
			setItems(renumberedItems);

			const pagesPayload = createPagesPayload(renumberedItems);
			dispatch(repaginateElements({ courseId, pages: pagesPayload }));
		}
	};

	const getPageHeader = (item) => (
		<div className={headerContainer}>
			<div className="headerText">
				<div>
					<span>Page: </span>
					{item.name}
				</div>
			</div>
		</div>
	);

	const getInitElementNumber = (element) => {
		const page = pages.entities[element.initPageId];

		if (!page && changes.pages[element.initPageId]?.includes(ChangesStatus.Deleted))
			return '(deleted)';

		return page.currentPageNumber;
	};

	const getPageElement = (item, pageId) => (
		<div className="pageElement">
			<div className="header">
				<span>
					{getElementHumanName(config, pages.entities[pageId].page_type, item) || item.type}{' '}
					{getElementSubtype(item)}
					{isDoubleElement(item.type) && (item as DoubleElements).direction === 'in' && (
						<BsArrowBarRight />
					)}
					{isDoubleElement(item.type) && (item as DoubleElements).direction === 'out' && (
						<BsArrowBarLeft />
					)}
				</span>
				<span>
					{item.initPageId !== pageId ? (
						<Badge variant="info">
							{getInitElementNumber(item)} <BsArrowRight />{' '}
							{pages.entities[pageId].currentPageNumber}
						</Badge>
					) : (
						<>{pages.entities[pageId].currentPageNumber}</>
					)}
				</span>
			</div>
			<div className="preview">{getElementPreview(item)}</div>
		</div>
	);

	const getItems = (items) => {
		let currentPageFamilyId = items[0].family_id;

		return items.slice(1).map((item, index) => {
			if (item.type === 'NG::Traditional::Page') currentPageFamilyId = item.family_id;
			const pageId = currentPageFamilyId;

			return (
				<Draggable draggableId={item.family_id} index={index + 1} key={item.family_id}>
					{(provided) => (
						<div
							ref={provided.innerRef}
							className={item.indent ? 'indent' : ''}
							{...provided.draggableProps}
							{...provided.dragHandleProps}>
							{item.type === 'NG::Traditional::Page'
								? getPageHeader(item)
								: getPageElement(item, pageId)}
						</div>
					)}
				</Draggable>
			);
		});
	};

	return (
		<LoaderWrapper
			loading={!chapterPages.every((pageId) => !!pages.entities[pageId].elements)}
			height="80vh">
			{!!items.length && (
				<div className={reorderBlock}>
					{getPageHeader(items[0])}

					<DragDropContext onDragEnd={onDragEnd}>
						<DelayedDroppableWrapper droppableId={chapterId} key={chapterId} type="Page">
							{(provided) => (
								<div ref={provided.innerRef} {...provided.droppableProps}>
									{getItems(items)}
									{provided.placeholder}
								</div>
							)}
						</DelayedDroppableWrapper>
					</DragDropContext>
				</div>
			)}
		</LoaderWrapper>
	);
};

export default Repaginator;
